/**
 * Resend_Verification_Screen.tsx — SK Platform
 * Shown when a user signs in with correct credentials but an UNVERIFIED email
 * (their org is still inactive). The platform issues a session anyway, so this
 * screen can call POST /v1/auth/resend-verification — which re-sends the
 * verification email to that same logged-in user (no email argument needed;
 * the server reads it from the session, so there's no enumeration surface).
 *
 * The link in the email returns to this SPA (?mode=verify) → Verify_Email_Screen
 * → activates the org → login. This is the only forward path: the app stays
 * locked until the org is active.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, Platform } from 'react-native';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { AuthStackParamList } from '../../navigation/types';
import { CONTENT } from '../../utils/responsive';

import PrimaryButton from '../../components/primaryButton';
import { resendVerification, logoutUser, getCurrentUser, invalidateMeCache } from '../../services/auth/auth';
import { onEmailVerified } from '../../navigation/authChannel';
import { handleApiError } from '../../utils/errorHandler';
import { showSuccess } from '../../utils/successHandler';
import { useTheme } from '../../context/themeContext';
import AuthBrandHeader from '../../components/authBrandHeader';

const Resend_Verification_Screen: React.FC = () => {
  const { theme } = useTheme();
  const { colors } = theme;
  const navigation = useNavigation<NativeStackNavigationProp<AuthStackParamList, 'resendVerification'>>();
  const { params } = useRoute<RouteProp<AuthStackParamList, 'resendVerification'>>();
  // The signed-in user's email (display only — the resend call uses the session).
  const email = params?.email;

  const [isSending, setIsSending] = useState(false);
  const [sentOnce, setSentOnce] = useState(false);

  // When the user opens the verification link (which activates the org + marks
  // the email verified server-side), advance this screen straight to login —
  // the same handoff the "check your email" screen uses. Two paths: the instant
  // same-origin cross-tab signal, and a /v1/me poll that also works when the
  // link opens on another origin or device.
  const doneRef = useRef(false);
  const advanceToLogin = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    navigation.reset({ index: 0, routes: [{ name: 'login' }] });
  }, [navigation]);

  useEffect(() => onEmailVerified(() => advanceToLogin()), [advanceToLogin]);

  useEffect(() => {
    let cancelled = false;
    let errors = 0;
    const tick = async () => {
      try {
        invalidateMeCache(); // bypass the 5s /v1/me cache so we see the flip promptly
        const res: any = await getCurrentUser();
        if (!cancelled && (res?.data ?? res)?.user?.email_verified) advanceToLogin();
        errors = 0;
      } catch {
        if (++errors >= 3) clearInterval(id);
      }
    };
    const id = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [advanceToLogin]);

  // Leaving the gate: clear the half-session (so a reload can't re-enter as a
  // "logged-in" user) and return to login.
  const onBackToLogin = async () => {
    try { await logoutUser(); } finally { navigation.navigate('login'); }
  };

  const handleResend = async () => {
    if (isSending) return;
    setIsSending(true);
    try {
      const res = await resendVerification();
      setSentOnce(true);
      showSuccess(res, 'Verification email sent', 'Please check your inbox to continue.');
    } catch (err) {
      handleApiError(err, 'Could not resend', 'Something went wrong. Please try again in a moment.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <View style={[styles.root, { backgroundColor: colors.paper }]}>
      <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
        <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.line }]}>
          <AuthBrandHeader />

          <View style={[styles.iconCircle, { backgroundColor: colors.accentSoft, borderColor: colors.accentLine }]}><Text style={styles.icon}>✨</Text></View>

          <Text style={[styles.eyebrow, { color: colors.accent }]}>JUST ONE STEP LEFT</Text>
          <Text style={[styles.title, { color: colors.accent }]}>Verify to unlock your workspace</Text>
          <Text style={[styles.sub, { color: colors.ink2 }]}>
            Your account is ready — it just needs a quick email verification. Once verified, your
            organization activates instantly and your workspace is all yours.
          </Text>

          <View style={[styles.emailPill, { backgroundColor: colors.accentSoft, borderColor: colors.accentLine }]}>
            <Text style={[styles.emailPillLabel, { color: colors.accent }]}>Registered email</Text>
            <Text style={[styles.emailPillValue, { color: colors.ink }]}>{email || 'your registered email'}</Text>
          </View>

          {sentOnce && (
            <View style={[styles.sentBox, { backgroundColor: colors.successSoft, borderColor: colors.successLine }]}>
              <Text style={[styles.sentText, { color: colors.success }]}>
                ✓ A fresh verification link is on its way. Open it to activate your organization.
              </Text>
            </View>
          )}

          <PrimaryButton
            title={sentOnce ? 'Resend Email Again' : 'Resend Verification Email'}
            onPress={handleResend}
            loading={isSending}
            disabled={isSending}
            style={styles.btn}
          />

          <PrimaryButton
            title="Back to login"
            onPress={() => onBackToLogin?.()}
            variant="ghost"
            size="small"
            style={styles.backBtn}
            textStyle={[styles.backBtnText, { color: colors.accent }]}
          />

          <Text style={[styles.hint, { color: colors.ink4 }]}>The link stays valid for 15 minutes. Can't find it? Check your spam folder.</Text>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    ...(Platform.OS === 'web' ? ({ minHeight: '100vh' } as any) : {}),
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 16,
  },
  card: {
    width: '100%',
    maxWidth: CONTENT.form,
    borderRadius: 24,
    padding: 32,
    borderWidth: 1,
    alignItems: 'center',
    overflow: 'hidden',
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 8px 40px rgba(15,23,42,0.10)' } as any)
      : {
          shadowColor: '#0F172A',
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: 0.1,
          shadowRadius: 24,
          elevation: 8,
        }),
  },
  iconCircle: {
    width: 60, height: 60, borderRadius: 30,
    borderWidth: 1, alignItems: 'center', justifyContent: 'center', marginBottom: 16,
  },
  icon: { fontSize: 28 },
  eyebrow: {
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 2,
    marginBottom: 6,
  },
  title: {
    fontSize: 26,
    fontWeight: '900',
    marginBottom: 10,
    textAlign: 'center',
    letterSpacing: -0.8,
    lineHeight: 32,
  },
  sub: {
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 18,
  },
  emailPill: {
    width: '100%',
    borderWidth: 1,
    borderRadius: 14,
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: 'center',
    marginBottom: 16,
  },
  emailPillLabel: { fontSize: 12, fontWeight: '600', marginBottom: 2 },
  emailPillValue: { fontSize: 15, fontWeight: '800', letterSpacing: -0.2 },
  sentBox: {
    width: '100%',
    borderWidth: 1,
    borderRadius: 12,
    padding: 14,
    marginBottom: 16,
  },
  sentText: { fontSize: 13, lineHeight: 20, fontWeight: '600', textAlign: 'center' },
  btn: { width: '100%' },
  backBtn: {
    width: 'auto' as any,
    minHeight: 'auto' as any,
    marginTop: 14,
    marginBottom: 6,
  },
  backBtnText: {
    textTransform: 'none' as any,
    fontWeight: '700',
    fontSize: 14,
    letterSpacing: 0,
  },
  hint: { fontSize: 12, textAlign: 'center', marginTop: 4, lineHeight: 18 },
});

export default Resend_Verification_Screen;
